/**
 * Recent-search history (feature #7).
 *
 * A tiny most-recent-first list of the terms the user actually searched,
 * persisted so it survives reloads and offers one-tap re-runs. Stored in
 * `localStorage` on the user's own device — the query text is theirs and never
 * leaves the browser (the telemetry path already scrubs it as PII, §11).
 *
 * De-duplicated case-insensitively (searching "radiohead" twice keeps one
 * entry, moved to the front) and capped, so it stays a short, useful list
 * rather than an ever-growing log. Every storage access is guarded: a blocked
 * or full `localStorage` (Safari private mode, quota) degrades to an in-memory
 * list for the session instead of throwing into the search box.
 *
 * @module
 */

/** The `localStorage` surface this module touches — injectable for tests. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SearchHistory {
  /** Terms, most-recent first. */
  list(): string[];
  /** Record a term (trimmed); no-ops on empty. Moves a repeat to the front. */
  add(term: string): void;
  /** Drop one term. */
  remove(term: string): void;
  /** Forget everything. */
  clear(): void;
}

export interface SearchHistoryDeps {
  readonly storage?: StorageLike | null;
  readonly key?: string;
  readonly max?: number;
}

export const SEARCH_HISTORY_KEY = 'sm.search-history';
export const SEARCH_HISTORY_MAX = 12;

function resolveStorage(deps: SearchHistoryDeps): StorageLike | null {
  if (deps.storage !== undefined) return deps.storage;
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    // Accessing `localStorage` can THROW (not just be null) in some privacy
    // configurations — treat that as "no storage".
    return null;
  }
}

export function createSearchHistory(deps: SearchHistoryDeps = {}): SearchHistory {
  const storage = resolveStorage(deps);
  const key = deps.key ?? SEARCH_HISTORY_KEY;
  const max = deps.max ?? SEARCH_HISTORY_MAX;

  // The session fallback when storage is unavailable — the feature still works
  // within a session, it just does not persist.
  let memory: string[] = [];

  function read(): string[] {
    if (storage === null) return [...memory];
    try {
      const raw = storage.getItem(key);
      if (raw === null) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((v): v is string => typeof v === 'string').slice(0, max);
    } catch {
      return [];
    }
  }

  function write(terms: string[]): void {
    memory = terms;
    if (storage === null) return;
    try {
      storage.setItem(key, JSON.stringify(terms));
    } catch {
      /* quota / blocked — the in-memory copy still serves this session */
    }
  }

  return {
    list() {
      return read();
    },
    add(term) {
      const trimmed = term.trim();
      if (trimmed === '') return;
      const lower = trimmed.toLowerCase();
      const withoutDupe = read().filter((t) => t.toLowerCase() !== lower);
      write([trimmed, ...withoutDupe].slice(0, max));
    },
    remove(term) {
      const lower = term.trim().toLowerCase();
      write(read().filter((t) => t.toLowerCase() !== lower));
    },
    clear() {
      memory = [];
      if (storage === null) return;
      try {
        storage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}
